import React, { useState, useEffect } from 'react';
import { PortvilleBill } from '../../../../types/portvilleBilling';

interface PenaltyInputModalProps {
  bill: PortvilleBill;
  isOpen: boolean;
  onClose: () => void;
  onSave: (billId: string, penaltyAmount: number) => Promise<void>;
}

const PenaltyInputModal: React.FC<PenaltyInputModalProps> = ({
  bill,
  isOpen,
  onClose,
  onSave
}) => {
  const [penaltyAmount, setPenaltyAmount] = useState<number>(bill.billItems.expectedPenalty || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');
  // 모달이 열릴 때마다 현재 예상 가산금으로 초기화
  useEffect(() => {
    if (isOpen) {
      setPenaltyAmount(bill.billItems.expectedPenalty || 0);
      setError('');
      setInputError('');
    }
  }, [isOpen, bill.billItems.expectedPenalty]);

  const handleSave = async () => {
    // 유효성 검사
    if (penaltyAmount < 0) {
      setError('가산금은 0원 이상이어야 합니다.');
      return;
    }

    if (!Number.isInteger(penaltyAmount)) {
      setError('가산금은 정수여야 합니다.');
      return;
    }

    if (penaltyAmount > 1000000) {
      setError('가산금은 1,000,000원을 초과할 수 없습니다.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await onSave(bill.id, penaltyAmount);
      onClose();
    } catch (error) {
      console.error('가산금 저장 실패:', error);
      setError('가산금 저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPenaltyAmount(bill.billItems.overduePenalty || 0);
    setError('');
    setInputError('');
    onClose();
  };

  // 저장 버튼 비활성화 조건
  const isSaveDisabled = isSaving || inputError !== '' || penaltyAmount < 0 || penaltyAmount > 1000000;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* 모달 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">가산금 입력</h2>
            <p className="text-gray-600 mt-1">
              {bill.roomNumber} - {bill.roomType === '시설물' && bill.facilityInfo 
                ? bill.facilityInfo.facilityName 
                : bill.residentInfo?.name || '정보 없음'}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* 청구서 정보 */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">청구서 정보</div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">청구 기간:</span>
                <span className="text-sm font-medium">
                  {bill.billingPeriod.year}년 {bill.billingPeriod.month}월
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">현재 총액:</span>
                <span className="text-sm font-medium">
                  {bill.totalAmount.toLocaleString()}원
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">현재 예상 가산금:</span>
                <span className="text-sm font-medium">
                  {(bill.billItems.expectedPenalty || 0).toLocaleString()}원
                </span>
              </div>
            </div>
          </div>


          {/* 가산금 입력 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              예상 가산금 (원) - 다음달에 청구할 금액
            </label>
            <input
              type="text"
              value={penaltyAmount === 0 ? '' : penaltyAmount.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setInputError(''); // 입력 시 에러 초기화
                
                // 숫자만 입력 허용 (빈 문자열 포함)
                if (value === '' || /^\d+$/.test(value)) {
                  const numValue = value === '' ? 0 : Number(value);
                  setPenaltyAmount(numValue);
                  
                  // 실시간 유효성 검사
                  if (numValue > 1000000) {
                    setInputError('가산금은 1,000,000원을 초과할 수 없습니다.');
                  }
                } else {
                  setInputError('숫자만 입력 가능합니다.');
                }
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                inputError ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="가산금을 입력하세요 (예: 10000)"
            />
            <div className="mt-1 text-sm text-gray-500">
              숫자만 입력 가능합니다. (예: 10000)
            </div>
            {inputError && (
              <div className="mt-1 text-sm text-red-600">
                {inputError}
              </div>
            )}
          </div>

          {/* 예상 총액 */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="text-sm font-medium text-blue-900 mb-2">예상 총액</div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">현재 총액:</span>
                <span>{bill.totalAmount.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">기존 가산금:</span>
                <span>{(bill.billItems.overduePenalty || 0).toLocaleString()}원</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">새 가산금:</span>
                <span>{penaltyAmount.toLocaleString()}원</span>
              </div>
              <div className="border-t border-blue-200 pt-1 mt-2">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-blue-900">최종 총액:</span>
                  <span className="text-blue-900">
                    {(bill.totalAmount - (bill.billItems.overduePenalty || 0) + penaltyAmount).toLocaleString()}원
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-red-800 text-sm">{error}</div>
            </div>
          )}
        </div>

        {/* 모달 푸터 */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                저장 중...
              </div>
            ) : (
              '저장'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PenaltyInputModal;
